/**
 * The rows the usage page draws beside the provider split: one per
 * environment, one per lane that is not in CMMD's ledger, one per driver of
 * the Local-only lane, and who priced the total.
 *
 * Split from `usageMerge` so the bucket totals and the lane rows can each be
 * read in one sitting. Pure.
 *
 * @module usageMergeLanes
 */
import type { UsageBucket, UsageDriver, UsageKind } from "@cmmd-forge/contracts";

/**
 * Which lane a bucket was counted in.
 *
 * `forge-agents` is agent work under a thread the reporting environment ran
 * that no Recorded turn carries: a Claude subagent's turns, a Codex subagent
 * rollout. It is a transcript bucket by origin, named apart so the page never
 * shows it as either ledger money or CLI work outside Forge. Forge work the
 * environment cannot tie to its own threads (`forge_unrecorded`) stays in the
 * Local-only lane and is named in the driver split.
 */
export type UsageLane = "cmmd" | "recorded" | "transcript" | "forge-agents";

/** Totals of one lane the page names on its own row. */
export interface LaneTotals {
  readonly costUsd: number;
  readonly totalTokens: number;
  readonly records: number;
}

/**
 * Totals for one Environment: every lane of one machine on one row.
 *
 * Keyed by the source that speaks for the bucket's runtime, so the three ways
 * a machine's spend arrives all land together: CMMD's ledger rows and the
 * machine's own Recorded turns, which both name the runtime, and its Local-only
 * lane, which names none because a CLI session has no runtime to name.
 * A runtime no source reported keeps a row of its own, named by the runtime,
 * so ledger spend from an Environment that is not answering never drops out of
 * the table.
 */
export interface EnvironmentTotals {
  /** The source that speaks for this Environment, or an unclaimed `runtimeId`. */
  readonly key: string;
  /** The runtime this Environment reported, or the unclaimed runtime itself. */
  readonly runtimeId: string | null;
  /** The source that speaks for this Environment, or the one that reported an unclaimed runtime. */
  readonly sourceId: string;
  readonly costUsd: number;
  readonly totalTokens: number;
  readonly records: number;
  readonly costShare: number;
  /** Lanes that contributed, in the order first seen. */
  readonly lanes: readonly UsageLane[];
  /** Records on this row whose tokens are known and whose rate is not. */
  readonly unpricedRecords: number;
  /**
   * Records on this row that carry no tokens. Zero also stands for a fleet
   * whose servers never sent the count, which is why only the page's own
   * `unreportedRecordsKnown` decides whether a count may be named.
   */
  readonly unreportedRecords: number;
}

/** Totals of the Local-only lane for one driver, most expensive first. */
export interface DriverTotals {
  readonly driver: UsageDriver;
  readonly costUsd: number;
  readonly totalTokens: number;
  readonly records: number;
  /** Share of the Local-only lane's cost, not of the page total. */
  readonly costShare: number;
}

/** How much of the cost CMMD priced, and how much this client estimated. */
export interface CostProvenance {
  readonly cmmdUsd: number;
  readonly localUsd: number;
}

interface MutableLaneTotals {
  costUsd: number;
  totalTokens: number;
  records: number;
}

interface EnvironmentCell extends MutableLaneTotals {
  readonly runtimeId: string | null;
  readonly sourceId: string;
  readonly lanes: UsageLane[];
  unpricedRecords: number;
  unreportedRecords: number;
}

/** Running lane totals across every owned bucket from every source. */
export interface LaneState {
  readonly environments: Map<string, EnvironmentCell>;
  readonly pendingSync: MutableLaneTotals;
  readonly localOnly: MutableLaneTotals;
  readonly forgeAgents: MutableLaneTotals;
  readonly drivers: Map<UsageDriver, MutableLaneTotals>;
  cmmdUsd: number;
  localUsd: number;
}

/** The finished rows, in the shape the page renders. */
export interface LaneRows {
  readonly environments: readonly EnvironmentTotals[];
  readonly pendingSync: LaneTotals;
  readonly localOnly: LaneTotals;
  readonly forgeAgents: LaneTotals;
  readonly drivers: readonly DriverTotals[];
  readonly costProvenance: CostProvenance;
}

function emptyLaneTotals(): MutableLaneTotals {
  return { costUsd: 0, totalTokens: 0, records: 0 };
}

export function emptyLaneState(): LaneState {
  return {
    environments: new Map(),
    pendingSync: emptyLaneTotals(),
    localOnly: emptyLaneTotals(),
    forgeAgents: emptyLaneTotals(),
    drivers: new Map(),
    cmmdUsd: 0,
    localUsd: 0,
  };
}

export const EMPTY_LANE_ROWS: LaneRows = {
  environments: [],
  pendingSync: { costUsd: 0, totalTokens: 0, records: 0 },
  localOnly: { costUsd: 0, totalTokens: 0, records: 0 },
  forgeAgents: { costUsd: 0, totalTokens: 0, records: 0 },
  drivers: [],
  costProvenance: { cmmdUsd: 0, localUsd: 0 },
};

/**
 * The lane a bucket was counted in: what it says about itself, else what the
 * fingerprint that owns its provider is. A summary from a build before the
 * `origin` key only ever had transcript and CMMD buckets.
 */
export function laneOf(bucket: UsageBucket, ownedKinds: ReadonlySet<string>): UsageLane {
  if (bucket.origin === "recorded") return "recorded";
  if (bucket.origin === "transcript")
    return bucket.driver === "forge" ? "forge-agents" : "transcript";
  return ownedKinds.has("cmmd") ? "cmmd" : "transcript";
}

/**
 * What a bucket's rows are rows of: what it says about itself, else what its
 * lane makes it.
 *
 * `docs/adr/usage-rows-have-a-kind.md` is why every row carries a kind at all.
 * A current build stamps `kind` on every bucket it produces — Recorded turns
 * from the serializer's own `usageKind`, transcript records from the same
 * sidechain/subagent-rollout fact the forge-agents lane is named from — so
 * that stamp is trusted first. The inference below is only the fallback for a
 * summary from an Environment on an older build whose scanner left the key
 * unstamped: the forge-agents lane is subagent work by definition, so the
 * same predicate `laneOf` uses to name that lane also names its kind, and
 * every other unstamped bucket is a turn, which is what a row recorded before
 * the ledger learned about kinds is.
 */
export function kindOf(bucket: UsageBucket): UsageKind {
  if (bucket.kind !== undefined) return bucket.kind;
  return bucket.origin === "transcript" && bucket.driver === "forge" ? "subagent" : "turn";
}

/** Total tokens of a bucket; reasoning is a subset of output and is not added again. */
export function bucketTokens(bucket: UsageBucket): number {
  return (
    bucket.totals.uncachedInputTokens +
    bucket.totals.cachedInputTokens +
    bucket.totals.cacheCreationTokens +
    bucket.totals.outputTokens
  );
}

function addLane(cell: MutableLaneTotals, bucket: UsageBucket, tokens: number): void {
  cell.costUsd += bucket.costUsd;
  cell.totalTokens += tokens;
  cell.records += bucket.records;
}

/**
 * Which source speaks for each runtime, built from the runtime ids the sources
 * report on their own fingerprints.
 *
 * This is what joins a machine's Local-only lane to its runtime-keyed lanes:
 * both end up on the row of the source that reported the runtime.
 */
export interface RuntimeOwnership {
  /** Source id per runtime id, from every source that reported one. */
  readonly ownerByRuntimeId: ReadonlyMap<string, string>;
  /** The runtime each of those sources speaks for, so its row can name one. */
  readonly runtimeIdBySourceId: ReadonlyMap<string, string>;
}

/** Which row a bucket belongs on, and how that row names itself. */
function environmentRowOf(
  bucket: UsageBucket,
  sourceId: string,
  runtimes: RuntimeOwnership,
): { readonly key: string; readonly runtimeId: string | null; readonly sourceId: string } {
  const bucketRuntimeId = bucket.runtimeId;
  if (bucketRuntimeId === undefined) {
    // A bucket that names no runtime is this source's own: its Local-only
    // lane, or Sidekick on CMMD's side.
    return {
      key: sourceId,
      runtimeId: runtimes.runtimeIdBySourceId.get(sourceId) ?? null,
      sourceId,
    };
  }
  const owner = runtimes.ownerByRuntimeId.get(bucketRuntimeId);
  // A runtime nobody reported is an Environment that is not answering this
  // page load. Its own row keeps the spend visible.
  if (owner === undefined) return { key: bucketRuntimeId, runtimeId: bucketRuntimeId, sourceId };
  return {
    key: owner,
    runtimeId: runtimes.runtimeIdBySourceId.get(owner) ?? bucketRuntimeId,
    sourceId: owner,
  };
}

/**
 * Folds a bucket into the rows the page draws beside the provider split:
 * its environment, the lane totals, the driver split of Local-only, and who
 * priced it.
 */
export function foldLanes(
  state: LaneState,
  bucket: UsageBucket,
  sourceId: string,
  lane: UsageLane,
  runtimes: RuntimeOwnership,
): void {
  const tokens = bucketTokens(bucket);
  const row = environmentRowOf(bucket, sourceId, runtimes);
  let environment = state.environments.get(row.key);
  if (environment === undefined) {
    environment = {
      runtimeId: row.runtimeId,
      sourceId: row.sourceId,
      lanes: [],
      unpricedRecords: 0,
      unreportedRecords: 0,
      ...emptyLaneTotals(),
    };
    state.environments.set(row.key, environment);
  }
  addLane(environment, bucket, tokens);
  // Per row, not per lane: the coverage cell answers how much of this
  // Environment's window is priced, and a missing count reads as none.
  environment.unpricedRecords += bucket.unpricedRecords;
  environment.unreportedRecords += bucket.unreportedRecords ?? 0;
  if (!environment.lanes.includes(lane)) environment.lanes.push(lane);
  if (lane === "recorded" && bucket.sync === "pending") addLane(state.pendingSync, bucket, tokens);
  if (lane === "forge-agents") addLane(state.forgeAgents, bucket, tokens);
  if (lane === "transcript") {
    addLane(state.localOnly, bucket, tokens);
    // A transcript bucket from a build that did not classify carries no
    // driver; it is an unknown launcher, which is what `other` means.
    const driver = bucket.driver ?? "other";
    let cell = state.drivers.get(driver);
    if (cell === undefined) {
      cell = emptyLaneTotals();
      state.drivers.set(driver, cell);
    }
    addLane(cell, bucket, tokens);
  }
  if (lane === "cmmd") state.cmmdUsd += bucket.costUsd;
  else state.localUsd += bucket.costUsd;
}

/** Freezes the running lane totals into the rows the page renders. */
export function finishLanes(state: LaneState, costUsd: number): LaneRows {
  const environments: EnvironmentTotals[] = [...state.environments.entries()]
    .map(([key, cell]) => ({
      key,
      runtimeId: cell.runtimeId,
      sourceId: cell.sourceId,
      costUsd: cell.costUsd,
      totalTokens: cell.totalTokens,
      records: cell.records,
      costShare: costUsd === 0 ? 0 : cell.costUsd / costUsd,
      lanes: cell.lanes,
      unpricedRecords: cell.unpricedRecords,
      unreportedRecords: cell.unreportedRecords,
    }))
    .toSorted((a, b) => b.costUsd - a.costUsd || b.totalTokens - a.totalTokens);

  const localCost = state.localOnly.costUsd;
  const drivers: DriverTotals[] = [...state.drivers.entries()]
    .map(([driver, cell]) => ({
      driver,
      costUsd: cell.costUsd,
      totalTokens: cell.totalTokens,
      records: cell.records,
      costShare: localCost === 0 ? 0 : cell.costUsd / localCost,
    }))
    .toSorted((a, b) => b.costUsd - a.costUsd || b.totalTokens - a.totalTokens);

  return {
    environments,
    pendingSync: { ...state.pendingSync },
    localOnly: { ...state.localOnly },
    forgeAgents: { ...state.forgeAgents },
    drivers,
    costProvenance: { cmmdUsd: state.cmmdUsd, localUsd: state.localUsd },
  };
}
